import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Product {
  id: number;
  title: string;
  sku: string;
  brand_id: number | null;
  category_id: number | null;
  carton_size: number;
  carton_stock: number;
  main_images: string[];
  status: string;
  brand_name?: string;
  category_name?: string;
  min_price_pair?: number;
}

interface ProductCardProps {
  product: Product;
  viewMode: 'grid' | 'list';
  onAddToCart?: () => void;
}

const ProductCard = ({ product, viewMode, onAddToCart }: ProductCardProps) => {
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const mainImage = product.main_images && product.main_images.length > 0
    ? product.main_images[0]
    : 'https://public.readdy.ai/ai/img_res/70e6f95ed5c5c78dca4f49b40e201f07.jpg';

  const isInStock = product.carton_stock > 0;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    if (!isInStock || quantity < 1) {
      return;
    }

    setIsAdding(true);

    try {
      // Find or create active cart
      let { data: carts } = await supabase
        .from('carts')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .limit(1);

      let cartId: number;

      if (!carts || carts.length === 0) {
        // Create new cart
        const { data: newCart, error: cartError } = await supabase
          .from('carts')
          .insert({ user_id: user.id, status: 'active' })
          .select('id')
          .single();

        if (cartError || !newCart) {
          throw new Error('Failed to create cart');
        }

        cartId = newCart.id;
      } else {
        cartId = carts[0].id;
      }

      // Check if product already in cart
      const { data: existingItem } = await supabase
        .from('cart_items')
        .select('id, quantity_cartons')
        .eq('cart_id', cartId)
        .eq('product_id', product.id)
        .single();

      if (existingItem) {
        // Update existing item
        const { error: updateError } = await supabase
          .from('cart_items')
          .update({ quantity_cartons: existingItem.quantity_cartons + quantity })
          .eq('id', existingItem.id);

        if (updateError) {
          throw new Error('Failed to update cart');
        }
      } else {
        // Insert new item
        const { error: insertError } = await supabase
          .from('cart_items')
          .insert({
            cart_id: cartId,
            product_id: product.id,
            quantity_cartons: quantity
          });

        if (insertError) {
          throw new Error('Failed to add to cart');
        }
      }

      // Show success message
      setSuccessMessage(`Added ${quantity} carton${quantity > 1 ? 's' : ''} to your cart.`);
      setQuantity(1);
      
      // Call parent callback if provided
      if (onAddToCart) {
        onAddToCart();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1) {
      setQuantity(value);
    }
  };

  if (viewMode === 'list') {
    return (
      <>
        <div className="block bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow overflow-hidden">
          <div className="flex gap-4 p-4">
            {/* Image */}
            <Link to={`/product?id=${product.id}`} className="w-32 h-32 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={mainImage}
                alt={product.title}
                className="w-full h-full object-cover object-top"
              />
            </Link>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Brand */}
                  {product.brand_name && (
                    <p className="text-xs text-gray-500 mb-1">{product.brand_name}</p>
                  )}

                  {/* Title */}
                  <Link to={`/product?id=${product.id}`}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2 hover:text-blue-600 transition-colors">
                      {product.title}
                    </h3>
                  </Link>

                  {/* SKU */}
                  <p className="text-sm text-gray-600 mb-2">SKU: {product.sku}</p>

                  {/* Category */}
                  {product.category_name && (
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {product.category_name}
                    </span>
                  )}
                </div>

                {/* Price & Stock */}
                <div className="text-right flex-shrink-0">
                  {product.min_price_pair ? (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500 mb-1">From</p>
                      <p className="text-2xl font-bold text-blue-600">
                        ${product.min_price_pair.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">per pair</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-2">Price on request</p>
                  )}

                  {/* Stock Status */}
                  <div className="mt-2">
                    {isInStock ? (
                      <div className="text-sm">
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <i className="ri-checkbox-circle-fill"></i>
                          In Stock
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {product.carton_stock} cartons
                        </p>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                        <i className="ri-close-circle-fill"></i>
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Carton Info & Quick Add */}
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  <i className="ri-box-3-line mr-1"></i>
                  {product.carton_size} pairs per carton
                </p>

                {/* Quick Add to Cart */}
                {isInStock && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={handleQuantityChange}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center cursor-pointer"
                    />
                    <button
                      onClick={handleAddToCart}
                      disabled={isAdding}
                      className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <>
                          <i className="ri-shopping-cart-line mr-1"></i>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  {successMessage}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Login Prompt Modal */}
        {showLoginPrompt && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginPrompt(false)}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-login-circle-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sign In Required</h3>
                <p className="text-gray-600 mb-6">
                  Please sign in to add products to your cart.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLoginPrompt(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <Link
                    to="/login"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                  >
                    Login / Register
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Grid View
  return (
    <>
      <div className="block bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow overflow-hidden group">
        {/* Image */}
        <Link to={`/product?id=${product.id}`} className="relative block aspect-square bg-gray-100 overflow-hidden">
          <img
            src={mainImage}
            alt={product.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          
          {/* Stock Badge */}
          {!isInStock && (
            <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
              Out of Stock
            </div>
          )}

          {/* Brand Badge */}
          {product.brand_name && (
            <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-700">
              {product.brand_name}
            </div>
          )}
        </Link>

        {/* Content */}
        <div className="p-4">
          {/* Category */}
          {product.category_name && (
            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded mb-2">
              {product.category_name}
            </span>
          )}

          {/* Title */}
          <Link to={`/product?id=${product.id}`}>
            <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2 min-h-[3rem] hover:text-blue-600 transition-colors">
              {product.title}
            </h3>
          </Link>

          {/* SKU */}
          <p className="text-sm text-gray-600 mb-3">SKU: {product.sku}</p>

          {/* Price */}
          {product.min_price_pair ? (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">From</p>
              <p className="text-2xl font-bold text-blue-600">
                ${product.min_price_pair.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">per pair</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-3">Price on request</p>
          )}

          {/* Carton Info */}
          <div className="pt-3 border-t border-gray-200 space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                <i className="ri-box-3-line mr-1"></i>
                Carton Size
              </span>
              <span className="font-medium text-gray-900">
                {product.carton_size} pairs
              </span>
            </div>

            {isInStock && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  <i className="ri-stack-line mr-1"></i>
                  Available
                </span>
                <span className="font-medium text-green-600">
                  {product.carton_stock} cartons
                </span>
              </div>
            )}
          </div>

          {/* Quick Add to Cart */}
          {isInStock && (
            <div className="mb-3 space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Quantity:</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center cursor-pointer"
                />
                <span className="text-xs text-gray-500 whitespace-nowrap">cartons</span>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={isAdding}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAdding ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <>
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Add to Cart
                  </>
                )}
              </button>
            </div>
          )}

          {/* View Details Button */}
          <Link
            to={`/product?id=${product.id}`}
            className="block w-full bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm text-center whitespace-nowrap"
          >
            View Details
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
              <i className="ri-checkbox-circle-line mr-1"></i>
              {successMessage}
            </div>
          )}
        </div>
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginPrompt(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-login-circle-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sign In Required</h3>
              <p className="text-gray-600 mb-6">
                Please sign in to add products to your cart.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <Link
                  to="/login"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                >
                  Login / Register
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
